import React, { useState, useEffect } from 'react';

export const MonthSelector = ({ selectedMonths, setSelectedMonths, onChange, currentFiscalYear, fiscalYear, isKojin = false }) => {
    // onChange と setSelectedMonths の両方に対応
    const updateMonths = setSelectedMonths || onChange;
    // currentFiscalYear と fiscalYear の両方に対応
    const year = currentFiscalYear || fiscalYear;
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState(null);

    const monthLabels = isKojin
        ? ['全', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12']
        : ['全', '4', '5', '6', '7', '8', '9', '10', '11', '12', '1', '2', '3'];

    const handleMonthMouseDown = (month) => {
        if (month === 0) {
            updateMonths([0]);
        } else {
            setIsDragging(true);
            setDragStart(month);
            updateMonths([month]);
        }
    };

    const handleMonthMouseEnter = (month) => {
        if (isDragging && month !== 0 && dragStart !== null) {
            const start = Math.min(dragStart, month);
            const end = Math.max(dragStart, month);
            const range = [];
            for (let i = start; i <= end; i++) {
                range.push(i);
            }
            updateMonths(range);
        }
    };

    const handleMonthMouseUp = () => {
        setIsDragging(false);
        setDragStart(null);
    };

    useEffect(() => {
        const handleGlobalMouseUp = () => {
            setIsDragging(false);
            setDragStart(null);
        };
        document.addEventListener('mouseup', handleGlobalMouseUp);
        return () => document.removeEventListener('mouseup', handleGlobalMouseUp);
    }, []);

    return (
        <div
            style={{display: 'flex', userSelect: 'none'}}
            onMouseUp={handleMonthMouseUp}
        >
            {monthLabels.map((label, idx) => {
                const month = idx;
                const isSelected = selectedMonths.includes(month);
                return (
                    <button
                        key={month}
                        className={`btn ${isSelected ? 'btn-primary' : 'btn-secondary'}`}
                        style={{
                            padding: '6px 0',
                            minWidth: '32px',
                            fontSize: '13px',
                            borderRadius: idx === 0 ? '4px 0 0 4px' : idx === 12 ? '0 4px 4px 0' : '0',
                            borderRight: idx < 12 ? 'none' : undefined,
                            margin: 0
                        }}
                        onMouseDown={() => handleMonthMouseDown(month)}
                        onMouseEnter={() => handleMonthMouseEnter(month)}
                    >
                        {label}
                    </button>
                );
            })}
        </div>
    );
};

// 月番号から日付範囲を計算するヘルパー
export const getDateRangeFromMonths = (selectedMonths, fiscalYear, isKojin = false) => {
    if (selectedMonths.includes(0) || selectedMonths.length === 0) {
        return { dateFrom: '', dateTo: '' };
    }

    const minMonth = Math.min(...selectedMonths);
    const maxMonth = Math.max(...selectedMonths);

    const getYearMonth = (m) => {
        if (isKojin) {
            return { year: fiscalYear, month: m };
        } else {
            if (m <= 9) {
                return { year: fiscalYear, month: m + 3 };
            } else {
                return { year: fiscalYear + 1, month: m - 9 };
            }
        }
    };

    const start = getYearMonth(minMonth);
    const end = getYearMonth(maxMonth);

    const dateFrom = `${start.year}-${String(start.month).padStart(2, '0')}-01`;
    const lastDay = new Date(end.year, end.month, 0).getDate();
    const dateTo = `${end.year}-${String(end.month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

    return { dateFrom, dateTo };
};

// 日付から月番号を取得するヘルパー
export const getMonthFromDate = (dateStr, fiscalYear, isKojin = false) => {
    if (!dateStr) return 0;
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;

    if (isKojin) {
        if (year === fiscalYear) {
            return month;
        }
        return 0;
    } else {
        if (year === fiscalYear && month >= 4) {
            return month - 3;
        }
        if (year === fiscalYear + 1 && month <= 3) {
            return month + 9;
        }
        return 0;
    }
};

// 今月の月番号を取得
export const getCurrentMonth = (fiscalYear, isKojin = false) => {
    const now = new Date();
    return getMonthFromDate(now.toISOString().split('T')[0], fiscalYear, isKojin);
};

export default MonthSelector;
